// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
  FormHelperText,
} from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { some, get } from 'lodash';
import queryString from 'query-string';
import { arrayOf, shape, string, func } from 'prop-types';

import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FormActionButtons,
  FormSelect,
  FormDefaultTextField,
} from '../../../../../components';
import {
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../../actions/authenticationSettings';
import { hasError, urlStringSlice } from '../../../../../utils';
import { SAML_BINDINGS, SAML_TOKEN_ATTRIBUTES } from '../../label';
import styles from '../../styles';
import Actions from '../../Actions';
import { ALERT_SUCCESS } from '../../../../../constants';

export const OldSamlProviderConfiguration = (props) => {
  const {
    classes,
    errors = [],
    authDetails,
    uuid,
    tabValue,
    onCancelDetails,
    notifyMessages,
    hasAttributeMappingFields,
    hasDetailsFields,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(get(props, 'location.search'));
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [acsUrl1, setAcsUrl1] = useState('');
  const [acsUrl2, setAcsUrl2] = useState('');
  const [acsUrl3, setAcsUrl3] = useState('');
  const [isAcsUrl, setIsAcsUrl] = useState(false);
  const [providerUrl, setProviderUrl] = useState('');
  const [isProviderUrl, setIsProviderUrl] = useState(false);
  const [samlBinding, setSamlBinding] = useState('0');
  const [isSamlBinding, setIsSamlBinding] = useState(false);
  const [tokenAttribute, setTokenAttribute] = useState('');
  const [isTokenAttribute, setIsTokenAttribute] = useState(false);
  const [tokenAttributeIn, setTokenAttributeIn] = useState('0');
  const [isTokenAttributeIn, setIsTokenAttributeIn] = useState(false);
  const [providerId, setProviderId] = useState('');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    if(tabValue === 'provider-configuration-tab' && get(authDetails, 'ConfigurationParams.acsUrl')) {
      if(localStorage.getItem('isUnSavedChanges') === 'true') {
        setIsUnsavedChanges(true);
      } else {
        setIsUnsavedChanges(false);
      }
      const acsUrlArray = urlStringSlice(get(authDetails, 'ConfigurationParams.acsUrl'), '/portalAuth');
      setAcsUrl1('https://');
      if(acsUrlArray.length > 0) setAcsUrl2(acsUrlArray[0]);
      if(acsUrlArray.length > 1) setAcsUrl3(acsUrlArray[1]);
      setProviderUrl(get(authDetails, 'ConfigurationParams.url'));
      setSamlBinding(get(authDetails, 'ConfigurationParams.samlBinding'));
      setTokenAttribute(get(authDetails, 'ConfigurationParams.samlTokenAttribute'));
      setTokenAttributeIn(get(authDetails, 'ConfigurationParams.samlTokenAttributeIn'));
      setProviderId(get(authDetails, 'ConfigurationParams.serviceProviderId'));
    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = !hasAttributeMappingFields(authDetails, queryParam) ||
      !hasDetailsFields(authDetails) || hasError(acsUrl2, true)
         || hasError(providerUrl, true) || hasError(samlBinding, true) ||
         hasError(tokenAttribute, true) ||hasError(tokenAttributeIn, true);
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, acsUrl2, providerUrl, samlBinding,
    tokenAttribute, tokenAttributeIn, tabValue]);

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'acsUrl': {
        setAcsUrl2(value);
        setIsAcsUrl(hasError(value, true));
        break;
      }
      case 'providerUrl': {
        setProviderUrl(value);
        setIsProviderUrl(hasError(value, true));
        break;
      }
      case 'samlBinding': {
        setSamlBinding(value);
        setIsSamlBinding(hasError(value, true));
        break;
      }
      case 'tokenAttribute': {
        setTokenAttribute(value);
        setIsTokenAttribute(hasError(value, true));
        break;
      }
      case 'tokenAttributeIn': {
        setTokenAttributeIn(value);
        setIsTokenAttribute(hasError(value, true));
        break;
      }
      case 'providerId': {
        setProviderId(value);
        break;
      }
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }


  const onNext = () => {
    const acsUrlError = hasError(acsUrl2, true);
    const providerUrlError = hasError(providerUrl, true);
    const samlBindingError = hasError(samlBinding, true);
    const tokenAttributeError = hasError(tokenAttribute, true);
    const tokenAttributeInError = hasError(tokenAttributeIn, true);
    if (some([
      acsUrlError, providerUrlError, samlBindingError, tokenAttributeError,
      tokenAttributeInError,
    ])) {
      setIsAcsUrl(acsUrlError);
      setIsProviderUrl(providerUrlError);
      setIsSamlBinding(samlBindingError);
      setIsTokenAttribute(tokenAttributeError);
      setIsTokenAttributeIn(tokenAttributeInError);
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      const aUrl = acsUrl1+acsUrl2+acsUrl3;
      const configParams = {
        acsUrl: aUrl,
        emailDomain: "",
        samlBinding: samlBinding,
        samlTokenAttribute: tokenAttribute,
        samlTokenAttributeIn: tokenAttributeIn,
        serviceProviderId: providerId,
        url: providerUrl,
      };

      const data = { ...authDetails, ...{
        ConfigurationParams: configParams,
        advancedConfigurations: {},
        } };

        props.setAuthSettings(data);
        const url = uuid ? `/publish/settings/authentication/edit/${uuid}/attribute-mapping${qParam}`
         : `/publish/settings/authentication/add/attribute-mapping${qParam}`;
        props.push(url);
    }
  }

  const onSave = () => {
    const aUrl = acsUrl1+acsUrl2+acsUrl3;
    const configParams = {
      acsUrl: aUrl,
      emailDomain: "",
      samlBinding: samlBinding,
      samlTokenAttribute: tokenAttribute,
      samlTokenAttributeIn: tokenAttributeIn,
      serviceProviderId: providerId,
      url: providerUrl,
    };

    const data = { ...authDetails, ...{
      ConfigurationParams: configParams,
      advancedConfigurations: {},
      } };
    if (uuid) {
      props.updateAuthScheme(uuid, data);
    } else {
      props.saveAuthScheme(uuid, data);
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }

  const onCopyClick = () => {
   const url = acsUrl1+acsUrl2+acsUrl3;
    copy(url);
    notifyMessages(getI18nFormattedMessage('label.copy.success.text', { 'name': 'url' }),
    ALERT_SUCCESS);
  }

  return(
    <div id="auth-settings-provider-container">
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.provider.configuration')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <div>
            <FormDefaultTextField
              id={'auth-settings-pc-old-saml-acs-url'}
              name={getI18nFormattedMessage('label.authorize.assertion.acs.url')}
              value={acsUrl2}
              error={isAcsUrl}
              defaultText={acsUrl1}
              defaultText1={acsUrl3}
              defaultTextClass={classes.defaultTextClass}
              defaultTextClass1={classes.defaultTextClass1}
              handleChange={(value) => handleChange('acsUrl', value, 255)}
              copyText={getI18nFormattedMessage('label.copy')}
              isDisabled={false}
              textInputClass={classes.defaultInputClass}
              onCopyClick={onCopyClick}
            />
            <div>
              <FormHelperText className={classes.helpText} error={isAcsUrl}>
                {intl.getI18nMessage('label.authorize.assertion.acs.url.help.text')}
              </FormHelperText>
            </div>
          </div>
          <FormTextField
            id={'auth-settings-pc-old-saml-provider-url'}
            name={getI18nFormattedMessage('label.authorize.identity.provider.url')}
            value={providerUrl}
            error={isProviderUrl}
            handleChange={(value) => handleChange('providerUrl', value)}
            helperText={getI18nFormattedMessage('label.authorize.identity.provider.url.help.text')}
          />
         <FormSelect
            id={'auth-settings-pc-old-saml-binding'}
            name={getI18nFormattedMessage('label.authorize.saml.binding')}
            value={samlBinding}
            error={isSamlBinding}
            data={SAML_BINDINGS}
            handleChange={(value) => handleChange('samlBinding', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.binding.help.text')}
            noNativeSelect
            noNoneOption={true}
          />
          <FormTextField
            id={'auth-settings-pc-old-saml-token-attribute'}
            name={getI18nFormattedMessage('label.authorize.saml.token.attribute')}
            value={tokenAttribute}
            error={isTokenAttribute}
            handleChange={(value) => handleChange('tokenAttribute', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.token.attribute.help.text')}
          />
          <FormSelect
            id={'auth-settings-pc-old-saml-token-attribute-in'}
            name={getI18nFormattedMessage('label.authorize.saml.token.attribute.in')}
            value={tokenAttributeIn}
            error={isTokenAttributeIn}
            data={SAML_TOKEN_ATTRIBUTES}
            handleChange={(value) => handleChange('tokenAttributeIn', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.token.attribute.in.help.text')}
            noNativeSelect
            noNoneOption={true}
          />
          <FormTextField
            id={'auth-settings-pc-old-saml-service-provider'}
            name={intl.getI18nMessage('label.authorize.saml.service.provider.id')}
            value={providerId}
            handleChange={(value) => handleChange('providerId', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.service.provider.id.help.text')}
          />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.next.button')}
        onCancelClick={onCancel}
        showAdditional={true}
        onAdditionalClick={onSave}
        isDisabledAdditional={isSaveDisabled}
        id="auth-settings-provider-config-buttons"
      />
    </div>
  );
}

OldSamlProviderConfiguration.propTypes = {
  classes: shape({}),
  errors: shape({}),
  assets: arrayOf(shape({})),
  authDetails:shape({}),
  location: shape({}),
  uuid: string,
  notifyMessages: func,
  setStateTabs: func,
  setAuthSettings: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  onCancelDetails: func,
  push: func,
  hasAttributeMappingFields: func,
  hasDetailsFields: func,
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = {
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(OldSamlProviderConfiguration);
